import { gql, useQuery } from '@apollo/client';
import React, { useCallback, useMemo } from 'react';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { Loading } from 'src/components/common/loading/Loading';
import { NoData } from 'src/components/common/noData/NoData';
import { EnterpriseExpirationBanners } from 'src/components/enterpriseExpirationBanners/EnterpriseExpirationBanners';
import { ErroredPathsList } from 'src/components/errors-tab/legacy/ErroredPathsList';
import { ErrorsChart } from 'src/components/errors-tab/legacy/ErrorsChart';
import { FeatureUnavailable } from 'src/components/planProtectedArea/featureUnavailable/FeatureUnavailable';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import {
  ServiceErrorStatsFilter,
  ServiceQueryStatsFilter,
  ServiceTracePathErrorsRefsFilter,
  ServiceTraceRefsFilter,
  UI__ErroredTracePathsQuery,
  UI__ErroredTracePathsQueryVariables,
} from 'src/lib/graphqlTypes/types';

export const queryErroredTracePathsQuery = gql<
  UI__ErroredTracePathsQuery,
  UI__ErroredTracePathsQueryVariables
>`
  query UI__ErroredTracePathsQuery(
    $serviceId: ID!
    $resolution: Resolution!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $errorFilter: ServiceErrorStatsFilter
    $queryFilter: ServiceQueryStatsFilter
    $noFilter: Boolean!
  ) {
    service(id: $serviceId) {
      id
      counts: statsWindow(from: $timeFrom, to: $timeTo) @skip(if: $noFilter) {
        # when queryId is null, the filter is ignored
        errorStats(filter: $errorFilter) {
          metrics {
            errorsCount
            requestsWithErrorsCount
          }
          group: groupBy {
            path
          }
        }
      }
      histograms: statsWindow(
        from: $timeFrom
        to: $timeTo
        resolution: $resolution
      ) {
        queryStats(filter: $queryFilter) @skip(if: $noFilter) {
          metrics {
            requestsWithErrorsCount
            uncachedRequestsCount
            cachedRequestsCount
          }
          timestamp # provides the start of the bucket
        }
      }
    }
  }
`;

function calculatePathErrorStats(data: UI__ErroredTracePathsQuery) {
  const errorStats = data.service?.counts?.errorStats;
  if (errorStats && Array.isArray(errorStats)) {
    return errorStats.map(
      ({
        metrics: { errorsCount, requestsWithErrorsCount },
        group: { path },
      }) => ({
        path,
        requestsWithErrorsCount,
        errorsCount,
      }),
    );
  }
  return [];
}

function ErrorsDisplay({
  serviceId,
  range,
  filter,
}: {
  serviceId: string;
  range: TimeRangeOption;
  filter:
    | ServiceErrorStatsFilter
    | ServiceTracePathErrorsRefsFilter
    | ServiceTraceRefsFilter
    | ServiceQueryStatsFilter;
}) {
  const { query, to, from, fastModeRange } =
    useRouteParams(insightsRouteConfig);
  const isInBackground = useIsAppInBackgroundContext();
  const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
    range,
    { from, to },
    undefined,
    fastModeRange,
  );

  const variables = useMemo(
    () => ({
      serviceId,
      timeFrom,
      timeTo,
      resolution,
      errorFilter: { queryId: query || null, ...filter },
      queryFilter: { queryId: query || null, ...filter },
      noFilter: !filter,
    }),
    [filter, query, resolution, serviceId, timeFrom, timeTo],
  );
  const { data, loading } = useQuery(queryErroredTracePathsQuery, {
    variables,
    pollInterval,
    skip: isInBackground,
    skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
  });
  const [currentAccountId] = useCurrentAccountId();
  const currentPlanData = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  if (!data || loading || currentPlanData.loading) {
    return (
      <div className="flex h-80 justify-center">
        <Loading size="md" />
      </div>
    );
  }

  const allowedTraces = !!currentPlanData?.currentPlan?.traces || false;

  const reports = data?.service?.histograms?.queryStats;
  const pathErrorStats = calculatePathErrorStats(data);

  if (reports && reports.length === 0) {
    return (
      <NoData
        message="No data"
        secondary="Try adjusting time range or resetting filters."
      />
    );
  }

  return allowedTraces ? (
    <div>
      {!query && <EnterpriseExpirationBanners />}
      {reports && reports.length > 0 ? (
        <ErrorsChart reports={reports} resolution={resolution} />
      ) : null}
      <div className="mt-8 flex flex-col gap-6">
        <ErroredPathsList
          pathErrorStats={pathErrorStats}
          expandFirstN={5}
          queryId={query}
          {...variables}
        />
      </div>
    </div>
  ) : (
    <FeatureUnavailable />
  );
}

export const ErroredPathsWithData = ({
  graphId,
  range,
}: {
  graphId: string;
  range: TimeRangeOption;
}) => {
  return (
    <FilterProvider>
      {({ filter }) => (
        <ErrorsDisplay filter={filter} serviceId={graphId} range={range} />
      )}
    </FilterProvider>
  );
};
